import { useMemo } from 'react';
import {
  formatTime,
  fractionOfDay,
  tapsOnDay,
  type TrackerAction,
  type TrackerTap,
} from '@awwyeah/shared';
import type { RosterMember } from '../useHouseholdRoster';

/**
 * Today, as one rail (design shape B, Dan 2026-08-18).
 *
 * The pills above answer "when was this last done"; the strip answers the *other* question
 * a household actually has — "what has already happened today, and by whom" — which is what
 * stops the dog being fed twice within ten minutes by two people in different rooms.
 *
 * Spans the **whole** local day rather than waking hours, so a 5am walk plots where it
 * really was instead of being clamped onto the left edge next to breakfast. Ticks at 6a /
 * 12p / 6p give the eye something to measure against without turning it into a chart.
 *
 * Emphatically not a timeline of record: it shows today and forgets. Yesterday's taps live
 * in the log, and nothing here aggregates, scores or compares one day against another.
 */
export function TodayStrip({
  actions,
  taps,
  day,
  now,
  getMember,
}: {
  actions: readonly TrackerAction[];
  taps: readonly TrackerTap[];
  day: Date;
  now: Date;
  getMember: (id: string) => RosterMember;
}): JSX.Element | null {
  const actionById = useMemo(
    () => new Map(actions.map((action) => [action.id, action])),
    [actions],
  );

  // Taps whose action has since been removed from the tracker stay in the log (see
  // `removeAction`) but have nothing to label them with, so they don't plot.
  const marks = useMemo(
    () => tapsOnDay(taps, day).filter((tap) => actionById.has(tap.actionId)),
    [taps, day, actionById],
  );

  if (marks.length === 0) return null;

  const nowFraction = fractionOfDay(now.toISOString(), day);
  const isToday = now.toDateString() === day.toDateString();

  return (
    <div className="flex flex-col gap-1" data-testid="tracker-today-strip">
      <div className="relative h-7">
        {/* The rail itself. */}
        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" />

        {[0.25, 0.5, 0.75].map((at) => (
          <span
            key={at}
            aria-hidden="true"
            className="absolute top-1/2 h-2 w-px -translate-y-1/2 bg-line"
            style={{ left: `${at * 100}%` }}
          />
        ))}

        {isToday && (
          <span
            aria-hidden="true"
            data-testid="tracker-strip-now"
            className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg-3"
            style={{ left: `${nowFraction * 100}%` }}
          />
        )}

        {marks.map((tap) => {
          const member = getMember(tap.byId);
          const action = actionById.get(tap.actionId);
          return (
            <span
              key={tap.id}
              data-testid="tracker-strip-mark"
              title={`${action?.label ?? 'Done'} · ${formatTime(tap.at)} · ${member.name}`}
              className="absolute top-1/2 flex h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-surface text-[9px] leading-none"
              style={{
                left: `${fractionOfDay(tap.at, day) * 100}%`,
                backgroundColor: member.color,
              }}
            >
              {action?.emoji ?? ''}
            </span>
          );
        })}
      </div>

      <div className="flex justify-between text-[10px] font-medium text-fg-3" aria-hidden="true">
        <span>12a</span>
        <span>6a</span>
        <span>12p</span>
        <span>6p</span>
        <span>12a</span>
      </div>
    </div>
  );
}
